import React from "react";
import {
  Building2,
  ShieldCheck,
  Hammer,
  Users,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

const About = () => {
  const aboutSections = [
    {
      number: "01",
      tag: "WHO WE ARE",
      title: "Building Strength. Delivering Trust.",
      description:
        "At Sanwariya Ji Steels & Infrastructure LLP, we specialize in premium steel fabrication and infrastructure solutions. We combine engineering expertise, skilled craftsmanship, and modern practices to deliver structures that are built to perform and last.",
      image:
        "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?auto=format&fit=crop&w=1200&q=80",
      icon: Building2,
      points: [
        "Premium steel fabrication solutions",
        "Strong engineering foundation",
        "Reliable project execution",
      ],
    },
    {
      number: "02",
      tag: "OUR EXPERTISE",
      title: "Precision That Makes a Difference.",
      description:
        "Our expertise covers structural steel fabrication, infrastructure development, and customized steel solutions. Every project is carefully planned and executed to meet technical requirements, quality standards, and client expectations.",
      image:
        "https://images.unsplash.com/photo-1531058020387-3be344556be6?auto=format&fit=crop&w=1200&q=80",
      icon: Hammer,
      points: [
        "Advanced fabrication techniques",
        "Customized structural solutions",
        "Quality-focused execution",
      ],
    },
    {
      number: "03",
      tag: "OUR APPROACH",
      title: "Engineered With Precision.",
      description:
        "We believe great infrastructure starts with proper planning and attention to detail. From initial design and material selection to fabrication and final execution, our team maintains a strong focus on safety, precision, and quality.",
      image:
        "https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=1200&q=80",
      icon: ShieldCheck,
      points: [
        "Detailed project planning",
        "Strict quality control",
        "Safety-driven processes",
      ],
    },
    {
      number: "04",
      tag: "OUR PEOPLE",
      title: "A Team You Can Rely On.",
      description:
        "Behind every successful project is a dedicated team of professionals. Our experienced workforce works together with transparency, accountability, and a shared commitment to delivering dependable results for every client.",
      image:
        "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1200&q=80",
      icon: Users,
      points: [
        "Experienced professionals",
        "Transparent communication",
        "Customer-first approach",
      ],
    },
  ];

  const stats = [
    {
      value: "100+",
      label: "Projects Completed",
    },
    {
      value: "40+",
      label: "Years Experience",
    },
    {
      value: "100+",
      label: "Expert Workforce",
    },
    {
      value: "100%",
      label: "Customer Satisfaction",
    },
  ];

  return (
    <section className="relative overflow-hidden pt-24 pb-16">
      {/* Background Decorations */}
      <div className="pointer-events-none absolute -left-32 -top-40 h-96 w-96 rounded-full bg-[var(--color-accent)]/10 blur-3xl" />

      <div className="pointer-events-none absolute right-0 top-[35%] h-96 w-96 rounded-full bg-[var(--color-secondary)]/10 blur-3xl" />

      <div className="pointer-events-none absolute bottom-0 left-1/3 h-80 w-80 rounded-full bg-[var(--color-dark)]/5 blur-3xl" />

      <div className="relative z-10 mx-auto max-w-7xl px-6">
        {/* =====================================================
            HERO
        ===================================================== */}
        <div className="mx-auto mb-14 max-w-4xl text-center">
          <span className="pill">Since excellence began</span>

          <h1 className="mt-5 text-5xl font-bold tracking-tight text-slate-800 md:text-6xl">
            About{" "}
            <span className="gradient-text">
              Us
            </span>
          </h1>

          <p className="mx-auto mt-5 max-w-3xl text-base leading-7 text-slate-600 md:text-lg">
            At{" "}
            <span className="font-semibold text-slate-800">
              Sanwariya Ji Steels & Infrastructure LLP
            </span>
            , we specialize in delivering premium steel fabrication and
            infrastructure solutions engineered with precision, innovation,
            and long-lasting quality.
          </p>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-5 text-sm font-medium text-slate-500">
            <div className="flex items-center gap-2">
              <CheckCircle2
                size={17}
                className="text-[var(--color-accent)]"
              />
              Quality Driven
            </div>

            <div className="flex items-center gap-2">
              <CheckCircle2
                size={17}
                className="text-[var(--color-accent)]"
              />
              Safety Focused
            </div>

            <div className="flex items-center gap-2">
              <CheckCircle2
                size={17}
                className="text-[var(--color-accent)]"
              />
              Client Focused
            </div>
          </div>
        </div>

        {/* =====================================================
            ZIG ZAG SECTIONS
        ===================================================== */}
        <div className="space-y-8">
          {aboutSections.map((item, index) => {
            const Icon = item.icon;
            const isReverse = index % 2 !== 0;

            return (
              <div
                key={item.number}
                className="group relative overflow-hidden rounded-[28px] border border-[var(--color-border)]/70 bg-white/80 shadow-sm backdrop-blur-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-lg"
              >
                {/* Accent Border */}
                <div className="absolute left-0 top-0 h-full w-1 bg-[var(--color-accent)] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                <div className="grid md:grid-cols-2">
                  {/* =================================================
                      CONTENT
                  ================================================= */}
                  <div
                    className={`flex flex-col justify-center p-7 md:p-9 lg:p-11 ${isReverse ? "md:order-2" : "md:order-1"
                      }`}
                  >
                    {/* Number + Tag */}
                    <div className="mb-4 flex items-center gap-3">
                      <span className="text-4xl font-bold leading-none text-[var(--color-accent)]/20">
                        {item.number}
                      </span>

                      <span className="h-px w-8 bg-[var(--color-accent)]/40" />

                      <span className="text-[10px] font-bold tracking-[0.18em] text-[var(--color-accent)]">
                        {item.tag}
                      </span>
                    </div>

                    {/* Icon */}
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--color-accent)]/10">
                      <Icon
                        size={22}
                        className="text-[var(--color-accent)]"
                      />
                    </div>

                    {/* Heading */}
                    <h2 className="max-w-xl text-2xl font-bold leading-tight text-slate-800 md:text-3xl">
                      {item.title}
                    </h2>

                    {/* Description */}
                    <p className="mt-4 max-w-xl text-sm leading-7 text-slate-600 md:text-[15px]">
                      {item.description}
                    </p>

                    {/* Points */}
                    <div className="mt-5 grid gap-2 sm:grid-cols-2">
                      {item.points.map((point) => (
                        <div
                          key={point}
                          className="flex items-center gap-2 text-xs font-medium text-slate-700"
                        >
                          <CheckCircle2
                            size={15}
                            className="shrink-0 text-[var(--color-accent)]"
                          />

                          <span>{point}</span>
                        </div>
                      ))}
                    </div>

                    {/* Button */}
                    <button
                      type="button"
                      className="group/btn mt-6 flex w-fit items-center gap-2 btn-primary"
                    >
                      Learn More

                      <ArrowRight
                        size={16}
                        className="transition-transform duration-300 group-hover/btn:translate-x-1"
                      />
                    </button>
                  </div>

                  {/* =================================================
                      IMAGE
                  ================================================= */}
                  <div
                    className={`relative h-[260px] overflow-hidden md:h-[390px] ${isReverse ? "md:order-1" : "md:order-2"
                      }`}
                  >
                    <img
                      src={item.image}
                      alt={item.title}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />

                    {/* Image Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />

                    {/* Number */}
                    <div className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-black/20 text-xs font-bold text-white backdrop-blur-md">
                      {item.number}
                    </div>

                    {/* Bottom Label */}
                    <div className="absolute bottom-5 left-5 right-5">
                      <div className="rounded-xl border border-white/20 bg-black/20 px-4 py-3 backdrop-blur-md">
                        <p className="text-[9px] font-semibold tracking-[0.18em] text-white/70">
                          SANWARIYA JI STEELS
                        </p>

                        <p className="mt-1 text-xs font-medium text-white">
                          Strength • Precision • Reliability
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* =====================================================
            STATS
        ===================================================== */}
        <div className="mt-14">
          <div className="mb-7 text-center">
            <span className="text-[10px] font-bold tracking-[0.2em] text-[var(--color-accent)]">
              OUR JOURNEY
            </span>

            <h2 className="mt-2 text-2xl font-bold text-slate-800 md:text-3xl">
              Numbers That Speak For Us
            </h2>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-4">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="group rounded-[20px] border border-[var(--color-border)]/70 bg-white/80 p-5 text-center shadow-sm backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
              >
                <h3 className="text-3xl font-bold text-[var(--color-accent)] md:text-4xl">
                  {stat.value}
                </h3>

                <p className="mt-2 text-xs font-medium text-slate-600">
                  {stat.label}
                </p>

                <div className="mx-auto mt-3 h-1 w-6 rounded-full bg-[var(--color-accent)]/30 transition-all duration-300 group-hover:w-12 group-hover:bg-[var(--color-accent)]" />
              </div>
            ))}
          </div>
        </div>

        {/* =====================================================
            CTA
        ===================================================== */}
        <div className="mt-12 overflow-hidden rounded-[28px] bg-slate-900 p-7 md:p-9">
          <div className="grid items-center gap-6 md:grid-cols-[1fr_auto]">
            <div>
              <span className="text-[10px] font-bold tracking-[0.2em] text-[var(--color-accent)]">
                LET'S BUILD TOGETHER
              </span>

              <h2 className="mt-2 text-2xl font-bold text-white md:text-3xl">
                Have a project in mind?
              </h2>

              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300">
                Let's discuss your steel fabrication or infrastructure
                requirements and build a solution that delivers strength,
                reliability, and long-term value.
              </p>
            </div>

            <button
              type="button"
              className="group flex w-fit items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition-all duration-300 hover:bg-[var(--color-accent)] hover:text-white"
            >
              Contact Us

              <ArrowRight
                size={17}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;